import { FC, useMemo } from "react";

import { FramerTooltip } from "@ryde/controls";
import { hasKeys, TSystemParamsDisplayWithCategories } from "@ryde/shared";
import { blueMui, greenMui, orangeMui, purpleMui, tealMui } from "@ryde/theme";

interface IProductionLogsStatsTabsSystemParams {
  systemParams?: TSystemParamsDisplayWithCategories;
}

interface ICategoryColor {
  backgroundColor: string;
  color: string;
  borderColor: string;
}

const getCategoryColor = (category: string): ICategoryColor => {
  switch (category) {
    case "General":
      return {
        backgroundColor: greenMui._100,
        color: greenMui._700,
        borderColor: greenMui._300,
      };
    case "Rides":
      return {
        backgroundColor: blueMui._100,
        color: blueMui._700,
        borderColor: blueMui._300,
      };
    case "Production":
      return {
        backgroundColor: orangeMui._100,
        color: orangeMui._700,
        borderColor: orangeMui._300,
      };
    case "Map":
      return {
        backgroundColor: purpleMui._100,
        color: purpleMui._700,
        borderColor: purpleMui._300,
      };
    case "Shifts":
      return {
        backgroundColor: tealMui._100,
        color: tealMui._700,
        borderColor: tealMui._300,
      };
    default:
      return {
        backgroundColor: greenMui._100,
        color: greenMui._700,
        borderColor: greenMui._300,
      };
  }
};

export const ProductionLogsStatsTabsSystemParams: FC<
  IProductionLogsStatsTabsSystemParams
> = ({ systemParams }) => {
  const sortedSystemParams = useMemo(() => {
    if (!systemParams || !hasKeys(systemParams)) {
      return [];
    }

    const paramsArray = Object.entries(systemParams)
      .filter(([_, param]) => param !== undefined && param !== null)
      .map(([key, param]) => {
        if (
          param &&
          typeof param === "object" &&
          "value" in param &&
          "category" in param
        ) {
          return {
            key,
            value: param.value,
            category: String(param.category),
          };
        } else {
          // Fallback for legacy format
          return {
            key,
            value: param,
            category: "General",
          };
        }
      });

    return paramsArray.sort((a, b) => {
      const aCategory = a.category || "";
      const bCategory = b.category || "";
      return aCategory.localeCompare(bCategory);
    });
  }, [systemParams]);

  if (!systemParams || Object.keys(systemParams).length === 0) {
    return (
      <div className="log__stats-tab-panel">
        <div className="log__metrics-section">
          <div className="log__metrics-section-title">
            System params applied
          </div>
          <div className="log__metrics-map-stats">
            <div className="log__metrics-map-item">
              <span className="log__metrics-map-label">
                No system parameters available
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="log__stats-tab-panel">
      <div className="log__metrics-section">
        <div className="log__metrics-section-title">System params applied</div>
        <div className="log__metrics-map-stats">
          {sortedSystemParams.map(({ key, value, category }) => {
            const fullValue = Array.isArray(value)
              ? value.join(", ")
              : String(value);

            const colors = getCategoryColor(category);

            return (
              <div key={key} className="log__metrics-map-item">
                <span className="log__metrics-map-label">{key}</span>
                <FramerTooltip content={`${category}: ${fullValue}`}>
                  <span
                    className="log__system-param-value truncate-text"
                    style={{
                      backgroundColor: colors.backgroundColor,
                      color: colors.color,
                      borderColor: colors.borderColor,
                    }}
                  >
                    {fullValue}
                  </span>
                </FramerTooltip>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
